import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { GameCategory, GameCategoryApi, LoopBackConfig } from "@lbsdk/*";
import { ConfirmationDialogComponent } from "@app/main/content/confirmation-dialog/confirmation-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CategoryFormComponent } from "@app/main/content/general-settings/game-categories/category-form/category-form.component";
import { FormGroup } from "@angular/forms";
import { environment } from "@env/environment";
import { GeneralSettingsService } from "@app/main/content/general-settings/general-settings.service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { scan, takeUntil } from "rxjs/operators";
import { MatSelect } from "@angular/material";

@Component({
  selector: "game-categories",
  templateUrl: "./game-categories.component.html",
  styleUrls: ["./game-categories.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class GameCategoriesComponent implements OnInit, OnChanges {
  @Input("categories") categories: GameCategory[];
  p = 1;
  breakpoint: number;
  fileNameDialogRef: any;
  categoryData: any;
  categoriesUploadPath: string;

  limit = 5;
  offset = 0;
  options = new BehaviorSubject<GameCategory[]>([]);
  options$: Observable<GameCategory[]>;
  categoriesCount;

  isLoading = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  s3BucketUrl = environment.s3BucketUrl;

  private readonly PIXEL_TOLERANCE = 3.0;

  @ViewChild("categorySelect") selectElem: MatSelect;

  constructor(
    private gameCategoryApi: GameCategoryApi,
    private dialog: MatDialog,
    private fuseSplashScreenService: FuseSplashScreenService,
    private snackBar: MatSnackBar,
    private generalSettingService: GeneralSettingsService
  ) {
    this.gameCategoryApi
      .find({ limit: this.limit, skip: 0 })
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(
        (el: GameCategory[]) => {
          this.options.next(el);
          this.options$ = this.options.asObservable().pipe(
            scan((acc, curr) => {
              return [...acc, ...curr];
            }, [])
          );
        },
        (error) => {
          console.log("error...", error);
          return;
        }
      );
  }

  ngOnInit() {
    this.selectElem.openedChange.subscribe(() =>
      this.registerPanelScrollEvent()
    );
    // this.getNextBatch();
    this.onResize(window.innerWidth);
    this.categoriesCount = this.generalSettingService.noOfCategories;
    this.categoriesUploadPath =
      environment.mainContainer + "/" + environment.categories;
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.categories = changes.categories.currentValue;
  }

  registerPanelScrollEvent() {
    const panel = this.selectElem.panel.nativeElement;
    panel.addEventListener("scroll", (event) => this.loadNextOnScroll(event));
  }

  loadNextOnScroll(event) {
    if (this.hasScrolledToBottom(event.target)) {
      this.gameCategoryApi
        .find({
          limit: this.limit,
          skip: this.offset + this.limit,
        })
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(
          (data: any) => {
            this.options.next(data);
            this.offset += this.limit;
          },
          (error) => {
            console.log("error...", error);
            return;
          }
        );
    }
  }

  private hasScrolledToBottom(target): boolean {
    return (
      Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) <
      this.PIXEL_TOLERANCE
    );
  }

  onSelect($event) {
    this.isLoading = true;
    if ($event && $event.value !== null) {
      this.categories = [];
      this.categories.push($event.value);
      this.isLoading = false;
    } else {
      this.categories = [];
      this.generalSettingService.getCategories().then(
        (categories: GameCategory[]) => {
          this.categories = categories;
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
          console.log("error...", error);
        }
      );
    }
  }

  onResize(event) {
    this.breakpoint = this.generalSettingService.onResize(event);
  }

  categoryDelete(event, categoryId) {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: "Are you sure you want to Delete?",
      },
    });
    this.fileNameDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        // category deleting on selecting yes from confirmation dialog
        if (!result) return;

        this.fuseSplashScreenService.show();

        try {
          let response = await this.gameCategoryApi
            .deleteById(categoryId)
            .toPromise();
          if (!response) {
            this.fuseSplashScreenService.hide();
            return;
          }

          let categories: any = await this.gameCategoryApi.find().toPromise();
          this.fuseSplashScreenService.hide();
          if (!categories) return;

          this.categories = categories;
          this.generalSettingService.onCategoriesChanged.next(this.categories);

          this.snackBar.open("Category deleted successfully", "OK", {
            verticalPosition: "top",
            duration: 2000,
          });
        } catch (error) {
          this.snackBar.open("Problem deleting category", "ERROR", {
            verticalPosition: "top",
            duration: 2000,
          });
          return error;
        }
      });
  }

  openDialog(action?: string, category?: any) {
    this.fileNameDialogRef = this.dialog.open(CategoryFormComponent, {
      panelClass: "contact-form-dialog",
      data: {
        action: action || "new",
        categoryData: action === "edit" ? category : null
      }
    });
  
    this.fileNameDialogRef.afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((response: any) => {
        if (!response) {
          return;
        }
  
        const {name, image} = response.data;
  
        this.categoryData = {
          name: name ? name.toLowerCase() : null,
          image: image
        };
  
        this.categoryImageupload(category ? category.id : null);
      });
  }

  categoryImageupload(categoryId?: string) {
    this.fuseSplashScreenService.show();

    let imageName: string | null = null;
    const hasNewImage =
      !!( this.categoryData &&
        this.categoryData.image &&
        this.categoryData.image._files &&
        this.categoryData.image._files.length > 0
      );

    if (!(this.categoryData && this.categoryData.name )|| !this.categoryData.name.trim()) {
      this.fuseSplashScreenService.hide();
      this.snackBar.open("Category name is required", "ERROR", {
        verticalPosition: "top",
        duration: 2000,
      });
      return;
    }

    const saveCategory = () => {
      const payload: any = {
        name: this.categoryData.name,
      };

      if (imageName) {
        payload.image = imageName;
      }

      if (categoryId) {
        payload.id = categoryId;
      }

      this.gameCategoryApi.patchOrCreate(payload).subscribe(
        () => {
          this.gameCategoryApi.find().subscribe(
            (categories: GameCategory[]) => {
              this.categories = categories;
              this.generalSettingService.onCategoriesChanged.next(categories);
              this.fuseSplashScreenService.hide();
              this.snackBar.open(
                categoryId
                  ? "Category updated successfully"
                  : "Category created successfully",
                "OK",
                {
                  verticalPosition: "top",
                  duration: 2000,
                }
              );
            },
            () => {
              this.fuseSplashScreenService.hide();
              this.snackBar.open("Problem fetching categories", "ERROR", {
                verticalPosition: "top",
                duration: 2000,
              });
            }
          );
        },
        () => {
          this.fuseSplashScreenService.hide();
          this.snackBar.open("Problem saving category", "ERROR", {
            verticalPosition: "top",
            duration: 2000,
          });
        }
      );
    };

    if (hasNewImage) {
      const file = this.categoryData.image._files[0];
      const formData = new FormData();
      formData.append("file", file);

      const url =
        LoopBackConfig.getPath() +
        "/" +
        LoopBackConfig.getApiVersion() +
        "/containers/" +
        encodeURIComponent(this.categoriesUploadPath) +
        "/upload";

      this.generalSettingService.uploadCategoryImage(url, formData).subscribe(
        (res: any) => {
          if (
            res &&
            res.result &&
            res.result.files &&
            res.result.files.file &&
            res.result.files.file.length
          ) {
            imageName = res.result.files.file[0].providerResponse.name;
          }

          saveCategory();
        },
        () => {
          this.fuseSplashScreenService.hide();
          this.snackBar.open("Image upload failed", "ERROR", {
            verticalPosition: "top",
            duration: 2000,
          });
        }
      );
    } else {
      saveCategory();
    }
  }

}
